/** @format */

import {ReactNode, useEffect, useRef, useState} from 'react';

import {Box, Popper} from '@mui/material';

interface HintPopperProps {
  /** ref на элемент-якорь */
  anchorRef: React.RefObject<HTMLElement>;
  /** Контент подсказки */
  content: ReactNode;
  /** Позиция относительно anchor */
  placement?: 'bottom-start' | 'bottom' | 'bottom-end' | 'top-start' | 'top' | 'top-end';
  /** Задержка скрытия (мс) */
  leaveDelay?: number;
  /** Отступ Popper от anchor */
  offset?: [number, number];
}

export const HoverDialog = ({
  anchorRef,
  content,
  placement = 'bottom-start',
  leaveDelay = 100,
  offset = [0, 0],
}: HintPopperProps) => {
  const [open, setOpen] = useState(false);
  const overAnchor = useRef(false);
  const overPopper = useRef(false);
  const closeTimer = useRef<number>();

  const tryClose = () => {
    window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      if (!overAnchor.current && !overPopper.current) {
        setOpen(false);
      }
    }, leaveDelay);
  };

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;

    const onEnter = () => {
      overAnchor.current = true;
      setOpen(true);
    };

    const onLeave = () => {
      overAnchor.current = false;
      tryClose();
    };

    anchor.addEventListener('mouseenter', onEnter);
    anchor.addEventListener('mouseleave', onLeave);

    return () => {
      window.clearTimeout(closeTimer.current);
      anchor.removeEventListener('mouseenter', onEnter);
      anchor.removeEventListener('mouseleave', onLeave);
    };
  }, [anchorRef, leaveDelay, tryClose]);

  return (
    <Popper
      open={open}
      anchorEl={anchorRef.current}
      placement={placement}
      modifiers={[{name: 'offset', options: {offset}}]}
      sx={{zIndex: 1300}}
    >
      <Box
        onMouseEnter={() => {
          overPopper.current = true;
          window.clearTimeout(closeTimer.current);
        }}
        onMouseLeave={() => {
          overPopper.current = false;
          tryClose();
        }}
      >
        {content}
      </Box>
    </Popper>
  );
};
